import React from "react";
import { useSettings } from "../context/SettingsContext";

export default function AdminOverview({ stats, isLoading, error, onRetry, bookingsData, trends }) {
  const { settings } = useSettings();
  const cs = settings.currency_symbol || '$';
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-900/20">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button
          onClick={onRetry}
          className="rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="rounded-xl border border-slate-200 p-4 dark:border-slate-800"
          >
            <div className="h-8 w-16 bg-slate-200 rounded animate-pulse dark:bg-slate-700"></div>
            <div className="h-4 w-20 bg-slate-200 rounded animate-pulse mt-2 dark:bg-slate-700"></div>
          </div>
        ))}
      </div>
    );
  }

  // Process bookings data for chart visualization
  const processBookingsData = () => {
    if (!bookingsData || bookingsData.length === 0) {
      return { byStatus: [], byMonth: [], byTour: [] };
    }

    // Group by status
    const statusCounts = bookingsData.reduce((acc, booking) => {
      acc[booking.status] = (acc[booking.status] || 0) + 1;
      return acc;
    }, {});

    const byStatus = Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count,
    }));

    // Group by month (last 6 months)
    const monthCounts = {};
    bookingsData.forEach((booking) => {
      const date = new Date(booking.booking_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthCounts[monthKey] = (monthCounts[monthKey] || 0) + 1;
    });

    const byMonth = Object.entries(monthCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, count]) => ({ month, count }));

    // Group by tour (top 5 most booked tours)
    const tourCounts = {};
    bookingsData.forEach((booking) => {
      if (booking.tour) {
        const tourTitle = booking.tour.title;
        if (!tourCounts[tourTitle]) {
          tourCounts[tourTitle] = { title: tourTitle, count: 0, revenue: 0 };
        }
        tourCounts[tourTitle].count += 1;
        // ✅ Revenue compté SEULEMENT pour les bookings completed (terminés)
        if (booking.status === 'completed') {
          tourCounts[tourTitle].revenue += parseFloat(booking.total_price || 0);
        }
      }
    });

    const byTour = Object.values(tourCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return { byStatus, byMonth, byTour };
  };

  const chartData = processBookingsData();
  const maxMonthCount = Math.max(...(chartData.byMonth.map(d => d.count) || [1]), 1);

  const statusColors = {
    pending: 'bg-yellow-500',
    confirmed: 'bg-green-500',
    cancelled: 'bg-red-500',
    completed: 'bg-blue-500',
  };

  const statusLabels = {
    pending: 'Pending',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    completed: 'Completed',
  };

  // Map trends to stat labels
  const trendMap = trends ? {
    'Users': { thisMonth: trends.users_this_month, lastMonth: trends.users_last_month },
    'Bookings': { thisMonth: trends.bookings_this_month, lastMonth: trends.bookings_last_month },
    'Reviews': { thisMonth: trends.reviews_this_month, lastMonth: trends.reviews_last_month },
  } : {};

  const getTrendPercent = (thisMonth, lastMonth) => {
    if (lastMonth === 0) return thisMonth > 0 ? 100 : 0;
    return Math.round(((thisMonth - lastMonth) / lastMonth) * 100);
  };

  // Icon and color mapping for each stat
  const statConfig = {
    'Tours': { icon: '🎫', gradient: 'from-blue-500 to-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', iconBg: 'bg-blue-100 dark:bg-blue-900/40', textColor: 'text-blue-600 dark:text-blue-400' },
    'Users': { icon: '👥', gradient: 'from-purple-500 to-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20', iconBg: 'bg-purple-100 dark:bg-purple-900/40', textColor: 'text-purple-600 dark:text-purple-400' },
    'Bookings': { icon: '📅', gradient: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', iconBg: 'bg-emerald-100 dark:bg-emerald-900/40', textColor: 'text-emerald-600 dark:text-emerald-400' },
    'Reviews': { icon: '⭐', gradient: 'from-amber-500 to-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20', iconBg: 'bg-amber-100 dark:bg-amber-900/40', textColor: 'text-amber-600 dark:text-amber-400' },
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const config = statConfig[stat.label];
          return (
            <div
              key={stat.label}
              className={`group relative rounded-xl border-2 border-slate-200 dark:border-slate-700 p-6 transition-all duration-300 hover:shadow-lg hover:scale-105 ${config.bg} overflow-hidden`}
            >
              {/* Background gradient effect */}
              <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${config.gradient} opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity`}></div>

              <div className="relative flex items-center justify-between">
                <div className="flex-1">
                  <div className={`text-xs font-bold uppercase tracking-wider ${config.textColor} mb-2 opacity-80`}>
                    {stat.label}
                  </div>
                  <div className={`text-4xl font-black ${config.textColor} group-hover:scale-110 transition-transform duration-300`}>
                    {stat.n}
                  </div>
                  {trendMap[stat.label] && (
                    <div className="mt-2 flex items-center gap-1">
                      {(() => {
                        const t = trendMap[stat.label];
                        const pct = getTrendPercent(t.thisMonth, t.lastMonth);
                        const isUp = pct > 0;
                        const isDown = pct < 0;
                        return (
                          <>
                            <span className={`text-xs font-semibold ${isUp ? 'text-green-600 dark:text-green-400' : isDown ? 'text-red-600 dark:text-red-400' : 'text-slate-500'}`}>
                              {isUp ? '↑' : isDown ? '↓' : '→'} {Math.abs(pct)}%
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              vs last month
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
                <div className={`flex items-center justify-center w-16 h-16 rounded-2xl ${config.iconBg} group-hover:rotate-12 transition-transform duration-300`}>
                  <span className="text-3xl">{config.icon}</span>
                </div>
              </div>

              {/* Animated bottom bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r ${config.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </div>
          );
        })}
      </div>

      {/* Monthly Revenue Trend */}
      {trends && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
            <span className="text-xl">💰</span>
            Revenue This Month
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">This Month</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {cs}{Number(trends.revenue_this_month || 0).toLocaleString()}
              </p>
            </div>
            <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">Last Month</p>
              <p className="text-2xl font-bold text-slate-700 dark:text-slate-300">
                {cs}{Number(trends.revenue_last_month || 0).toLocaleString()}
              </p>
            </div>
            <div className="text-center p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">Change</p>
              {(() => {
                const pct = getTrendPercent(trends.revenue_this_month || 0, trends.revenue_last_month || 0);
                const isUp = pct > 0;
                const isDown = pct < 0;
                return (
                  <p className={`text-2xl font-bold ${isUp ? 'text-green-600 dark:text-green-400' : isDown ? 'text-red-600 dark:text-red-400' : 'text-slate-500'}`}>
                    {isUp ? '↑' : isDown ? '↓' : '→'} {Math.abs(pct)}%
                  </p>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Bookings Charts */}
      {bookingsData && bookingsData.length > 0 && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Bookings by Status */}
            <div className="rounded-xl border border-slate-200 p-6 dark:border-slate-800">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                Bookings by Status
              </h3>
              <div className="space-y-3">
                {chartData.byStatus.map((item) => (
                  <div key={item.status}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600 dark:text-slate-300 capitalize">
                        {statusLabels[item.status] || item.status}
                      </span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {item.count}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2 dark:bg-slate-700">
                      <div
                        className={`h-2 rounded-full ${statusColors[item.status] || 'bg-slate-500'}`}
                        style={{
                          width: `${(item.count / bookingsData.length) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bookings by Month */}
            <div className="rounded-xl border border-slate-200 p-6 dark:border-slate-800">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                Bookings Trend (Last 6 Months)
              </h3>
              <div className="flex items-end justify-between gap-2 h-48">
                {chartData.byMonth.map((item) => (
                  <div key={item.month} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex flex-col justify-end items-center" style={{ height: '160px' }}>
                      <span className="text-xs font-semibold text-slate-900 dark:text-slate-100 mb-1">
                        {item.count}
                      </span>
                      <div
                        className="w-full bg-emerald-500 rounded-t-md transition-all hover:bg-emerald-600"
                        style={{
                          height: `${(item.count / maxMonthCount) * 100}%`,
                          minHeight: item.count > 0 ? '8px' : '0',
                        }}
                      />
                    </div>
                    <span className="text-xs text-slate-600 dark:text-slate-400">
                      {new Date(item.month + '-01').toLocaleDateString('en-US', {
                        month: 'short',
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
